import React from "react";
import { Box } from "@mui/material";
import { color, type, space } from "../../design/tokens";

/**
 * Typographic primitives.
 *
 * Every heading, label and paragraph on the site goes through one of these so
 * the scale can't drift. They render real semantic elements — `Display` takes
 * an explicit `as` because heading *level* is a document-structure decision
 * and must never be inferred from how big the text looks.
 */

/**
 * Small-caps eyebrow with a leading hairline rule. The rule is the signature
 * device that ties sections together; `bare` drops it for tight spots like
 * card headers.
 */
export const Label = ({ children, tone = "ink", bare = false, sx, ...rest }) => {
  const tones = {
    ink: { text: color.clay, rule: color.line },
    onInk: { text: color.clayLight, rule: color.lineOnInk },
    mute: { text: color.mute, rule: color.line },
  };
  const palette = tones[tone] || tones.ink;

  return (
    <Box
      component="p"
      sx={{
        ...type.label,
        color: palette.text,
        display: "flex",
        alignItems: "center",
        gap: 2,
        margin: 0,
        ...(bare
          ? {}
          : {
              "&::before": {
                content: '""',
                width: { xs: 28, md: 48 },
                height: "1px",
                backgroundColor: palette.rule,
                flexShrink: 0,
              },
            }),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/**
 * Display heading in Instrument Serif.
 *
 * `as` sets the element (required for correct h1/h2/h3 order), `size` sets the
 * visual step. Keeping them independent is what lets a page have exactly one
 * h1 without forcing it to be the biggest thing on screen.
 */
export const Display = ({
  children,
  as = "h2",
  size = "lg",
  tone = "ink",
  balance = true,
  sx,
  ...rest
}) => {
  const sizes = {
    xl: type.displayXl,
    lg: type.displayLg,
    md: type.displayMd,
    sm: type.displaySm,
  };
  const tones = { ink: color.ink, onInk: color.onInk, clay: color.clay };

  return (
    <Box
      component={as}
      sx={{
        ...(sizes[size] || sizes.lg),
        color: tones[tone] || tones.ink,
        margin: 0,
        // Avoids a single orphaned word on the last line of a big headline.
        // Falls back silently where unsupported.
        textWrap: balance ? "balance" : "pretty",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/**
 * The italic accent inside a display line. Instrument Serif's italic is a true
 * cut rather than a slant, so this reads as emphasis rather than as a glitch.
 * Used at most once per heading.
 */
export const Accent = ({ children, sx, ...rest }) => (
  <Box component="em" sx={{ fontStyle: "italic", ...sx }} {...rest}>
    {children}
  </Box>
);

/** Opening paragraph under a display heading. */
export const Lead = ({ children, tone = "ink", sx, ...rest }) => (
  <Box
    component="p"
    sx={{
      ...type.lead,
      color: tone === "onInk" ? color.onInkMute : color.graphite,
      maxWidth: space.measure,
      margin: 0,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

/** Body copy, held to a readable measure by default. */
export const Body = ({ children, tone = "ink", size = "md", sx, ...rest }) => (
  <Box
    component="p"
    sx={{
      ...(size === "sm" ? type.bodySm : type.body),
      color: tone === "onInk" ? color.onInkMute : color.graphite,
      maxWidth: space.measure,
      margin: 0,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

/** Photo captions and meta rows. */
export const Caption = ({ children, tone = "ink", sx, ...rest }) => (
  <Box
    component="p"
    sx={{
      ...type.caption,
      color: tone === "onInk" ? color.onInkMute : color.mute,
      margin: 0,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

/** Full-width hairline. The only divider used anywhere in the design. */
export const Rule = ({ tone = "ink", sx }) => (
  <Box
    aria-hidden="true"
    sx={{
      height: "1px",
      width: "100%",
      backgroundColor: tone === "onInk" ? color.lineOnInk : color.line,
      ...sx,
    }}
  />
);
